import React from 'react';
import Header from '@amzn/awsui-components-react/polaris/header';
import { Event, Source } from '@amzn/red-velvet-api';
import { useSearchParams } from 'react-router-dom';
import { useUser } from '../../hooks/user';
import { canManageGivehubEvent, canManageVCEvent } from '../../utils/shiftUtils';
import { EditEventLink } from '../shifts/actions';
import { SpaLink } from '../SpaLink';

interface EventTitleHeaderProps {
  event: Event;
  /** Analytics source passed to EditEventLink for manageable GIVEHUB events. */
  editSource?: string;
  /** data-aci-analytics-name for the (GIVEHUB) title link. */
  titleAnalyticsName?: string;
  /**
   * Edit control rendered for manageable VC events. VC edit flows differ per page
   * (e.g. attendance re-enters the wizard), so the caller supplies the control.
   */
  vcEditAction?: React.ReactNode;
}

/**
 * Shared event title header: links the title to the event details page for GIVEHUB events,
 * shows a plain title for VC events, and renders the appropriate edit affordance for coordinators.
 */
export function EventTitleHeader({
  event,
  editSource,
  titleAnalyticsName,
  vcEditAction,
}: EventTitleHeaderProps) {
  const user = useUser();
  const [searchParams] = useSearchParams();
  const selectedTimezone = searchParams.get('timezone');
  const urlFeatures = searchParams.get('features');

  const isGivehub = event.source === Source.GIVEHUB;

  let headerAction: React.ReactNode;
  if (isGivehub && canManageGivehubEvent(event, user)) {
    headerAction = <EditEventLink event={event} source={editSource} />;
  } else if (!isGivehub && canManageVCEvent(event, user)) {
    headerAction = vcEditAction;
  }

  const viewDetailsUrl = `/view-event?id=${event.eventId}${selectedTimezone ? '&timezone=' + selectedTimezone : ''}${urlFeatures ? '&features=' + urlFeatures : ''}`;

  return (
    <Header variant="h2" actions={headerAction}>
      {isGivehub ? (
        <SpaLink data-aci-analytics-name={titleAnalyticsName} href={viewDetailsUrl}>
          <span>{event.title}</span>
        </SpaLink>
      ) : (
        <span>{event.title}</span>
      )}
    </Header>
  );
}
